import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, Injector, OnDestroy, inject } from '@angular/core';
import {
  KeycloakLoginParams,
  navigateToKeycloakLogin,
} from '@app/finxone-web-frontend/app/lib/services/keycloak-wrapper-service/keycloak-login.utils';
import { Store } from '@ngxs/store';
import { KeycloakEventTypeLegacy, KeycloakOptions, KeycloakService } from 'keycloak-angular';
import { Subject, filter, firstValueFrom, switchMap, take } from 'rxjs';
import { IS_EXTERNAL_KEYCLOAK } from '../../../lib/http-constants';
import { UpdateCurrentAccountId } from '../../actions/account.action';
import { clearLocalStorageExcept } from '../../utils/utils';
import { WebFrontendConfig } from '../config-service/config.type';
import * as Sentry from '@sentry/angular';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { AutoDestroy } from '@finxone-platform/shared/base-types';

@Injectable({
  providedIn: 'root',
})
export class KeycloakWrapperService implements OnDestroy {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  private readonly keycloakService = inject(KeycloakService);
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly injector = inject(Injector);
  private readonly tabCloseInterval: ReturnType<typeof setInterval>;

  constructor() {
    const keycloakService = this.keycloakService;
    keycloakService.keycloakEvents$
      .pipe(filter((event) => event.type === KeycloakEventTypeLegacy.OnTokenExpired))
      .subscribe(() => {
        console.log('expired token ' + new Date());
        keycloakService
          .updateToken(5)
          .then((refreshed) => {
            if (refreshed) {
              console.log('refreshed ' + new Date());
              const token = keycloakService.getKeycloakInstance().token;
              if (token) sessionStorage.setItem('token', token);
              const refreshToken = keycloakService.getKeycloakInstance().refreshToken;
              if (refreshToken) sessionStorage.setItem('refreshToken', refreshToken);
            } else {
              console.error('Failed to refresh access token ' + new Date());
            }
          })
          .catch(() => {
            console.error('Failed to refresh access token ' + new Date());
          });
      });
    // Set up visibility change handling
    if (typeof document !== 'undefined') {
      // Check if we need to clear currentAccountId on page load
      const lastActiveTime = localStorage.getItem('lastActiveTime');
      const isWebView = this.isWebView();

      // Only clear if:
      // 1. Not in WebView, or
      // 2. In WebView but token is expired (real session end)
      if (lastActiveTime && !isWebView) {
        const timeDiff = Date.now() - parseInt(lastActiveTime);
        // If it's been more than 5 seconds since last activity, clear currentAccountId

        if (timeDiff > 5000) {
          this.clearCurrentAccountIdFromAccountState();
        }
      }

      // Update last active time when page becomes visible
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          localStorage.setItem('lastActiveTime', Date.now().toString());
        }
      });

      // Update last active time periodically while page is visible
      const updateLastActiveTime = () => {
        if (document.visibilityState === 'visible') {
          localStorage.setItem('lastActiveTime', Date.now().toString());
        }
      };
      this.tabCloseInterval = setInterval(updateLastActiveTime, 5000);
    }
  }

  ngOnDestroy(): void {
    if (this.tabCloseInterval) {
      clearInterval(this.tabCloseInterval);
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    document.removeEventListener('visibilitychange', () => {});
  }

  public idleTimeLogout() {
    console.log(`KeycloakWrapperService idleTimeLogout: clearLocalStorageExcept`);
    this.clearCurrentAccountIdFromAccountState();
    clearLocalStorageExcept([
      'account',
      'transactions',
      'beneficiary',
      'notifications',
      'lastLoggedInId',
      'APP_PREFERENCES',
      'firstTime',
      'lastSelectedOrg',
    ]);
    this.executeLogoutAndRedirect(window.location.origin + '/post-auth');
  }

  public login(options?: Keycloak.KeycloakLoginOptions, additionalParams?: KeycloakLoginParams) {
    return this.keycloakService.login(options);
  }

  public loginLink(options?: Keycloak.KeycloakLoginOptions, additionalParams?: KeycloakLoginParams) {
    navigateToKeycloakLogin(
      {
        baseUrl: this.keycloakService.getKeycloakInstance().authServerUrl ?? '',
        realm: this.keycloakService.getKeycloakInstance().realm ?? '',
        clientId: this.keycloakService.getKeycloakInstance().clientId ?? '',
        redirectUri: options?.redirectUri,
      },
      additionalParams,
    );
  }

  public init(options?: KeycloakOptions): Promise<boolean> {
    return this.keycloakService.init(options);
  }

  public getToken(): Promise<string> {
    return this.keycloakService.getToken();
  }

  public logout(redirectUri?: string | undefined): void {
    console.log(`KeycloakWrapperService logout: clearLocalStorageExcept`);

    this.clearCurrentAccountIdFromAccountState();
    clearLocalStorageExcept([
      'account',
      'transactions',
      'beneficiary',
      'notifications',
      'lastLoggedInId',
      'APP_PREFERENCES',
      'firstTime',
      'lastSelectedOrg',
    ]);
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');

    this.executeLogoutAndRedirect(redirectUri ?? window.location.origin);
  }

  public refreshInit(config: WebFrontendConfig) {
    // getBaseKeycloakConfig already prefers the window tokens the native shell
    // injects for biometric login, so a re-init just reuses the base config.
    this.keycloakService.init(this.getBaseKeycloakConfig(config));
  }

  public getBaseKeycloakConfig(config: WebFrontendConfig): KeycloakOptions {
    // Hand any existing session tokens to keycloak-js so the adapter holds the refresh
    // token and can renew (OnTokenExpired handler above / updateToken on 401). Without
    // this the webview flow, which restores tokens from the native shell / sessionStorage
    // instead of an SSO redirect, has a token-less adapter that can never refresh.
    // A login callback in the URL takes precedence over these inside keycloak-js init.
    // Window tokens are freshly injected by the native shell, so they win over storage.
    const token = readInjectedToken('accessToken') ?? readStoredToken('token');
    const refreshToken = readInjectedToken('refreshToken') ?? readStoredToken('refreshToken');
    const idToken = readInjectedToken('idToken') ?? readStoredToken('idToken');
    return {
      config: {
        url: config.api_endpoints['auth_service'],
        realm: config.auth.realm,
        clientId: config.auth.client_id,
      },
      initOptions: {
        enableLogging: false,
        flow: 'standard',
        checkLoginIframe: false,
        ...(token && refreshToken ? { token, refreshToken, idToken } : {}),
      },
    };
  }

  private executeLogoutAndRedirect(redirectUrl: string): void {
    redirectUrl = redirectUrl ?? window.location.origin;

    this.injector
      .get(ConfigService)
      .getApi('metadata_service')
      .pipe(
        take(1),
        switchMap((baseUrl) =>
          this.http.post(
            `${baseUrl}/users/logout`,
            {},
            {
              context: new HttpContext().set(IS_EXTERNAL_KEYCLOAK, false),
            },
          ),
        ),
      )
      .subscribe({
        next: () => {
          Sentry.addBreadcrumb({
            message: 'Logout via revokeAllOnlineUserSessions',
            level: 'info',
          });
          window.location.href = redirectUrl;
        },
        error: (err) => {
          Sentry.addBreadcrumb({
            message: 'Logout API failed, falling back to id_token_hint',
            level: 'warning',
          });
          Sentry.captureException(err);
          // Fallback to id_token_hint logout if endpoint fails
          const keycloak = this.keycloakService.getKeycloakInstance();
          const idToken = keycloak.idToken || sessionStorage.getItem('idToken');
          if (idToken) {
            window.location.href =
              `${keycloak.authServerUrl}realms/${keycloak.realm}/protocol/openid-connect/logout` +
              `?id_token_hint=${idToken}` +
              `&post_logout_redirect_uri=${encodeURIComponent(redirectUrl)}`;
          } else {
            this.keycloakService.logout(redirectUrl);
          }
        },
      });
  }

  private clearCurrentAccountIdFromAccountState() {
    this.store.dispatch(new UpdateCurrentAccountId(''));
  }

  private isWebView(): boolean {
    // Check for flutter WebView indicators
    const flutterWebView = (window as any).flutter_inappwebview;
    if (flutterWebView) {
      return true;
    } else {
      return false;
    }
  }

  public isLoggedIn(): boolean {
    return this.keycloakService.isLoggedIn();
  }

  public getKeycloakInstance(): Keycloak.KeycloakInstance {
    return this.keycloakService.getKeycloakInstance();
  }

  public isTokenExpired(minValidity?: number): boolean {
    return this.keycloakService.isTokenExpired(minValidity);
  }

  /**
   * Generic method to call endpoints from our custom keycloak extension
   * Includes logic for handling proxy for extension if running locally
   *
   * @param method
   * @param endpoint
   * @param options
   */
  public async useCustomKeycloakService<T>(
    method: 'GET' | 'POST' | 'PATCH',
    endpoint: string,
    options?: {
      data?: any;
      params?: any;
      headers?: Record<string, string>;
      isExternalKeycloak?: boolean;
    },
  ): Promise<T> {
    const token: string = await this.getToken();
    const keycloak: Keycloak.KeycloakInstance = this.getKeycloakInstance();

    const isLocalKeycloak: boolean = keycloak?.authServerUrl?.includes('localhost:8088') ?? false; // Check for local keycloak
    // Use relative path for localhost (proxy will handle it)
    // Use absolute path for all other environments
    const url: string = isLocalKeycloak
      ? `/auth/realms/${keycloak.realm}/${endpoint}`
      : `${keycloak.authServerUrl}/realms/${keycloak.realm}/${endpoint}`;

    return firstValueFrom(
      this.http.request<T>(method, url, {
        body: options?.data,
        params: options?.params,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        context: new HttpContext().set(IS_EXTERNAL_KEYCLOAK, options?.isExternalKeycloak ?? false),
      }),
    );
  }
}

/** Token injected on window by the native shell (Flutter webview / biometric login). */
function readInjectedToken(key: 'accessToken' | 'refreshToken' | 'idToken'): string | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const value = (window as any)[key];
  return typeof value === 'string' && value.length && value !== 'undefined' ? value : undefined;
}

/** Token persisted in sessionStorage by a previous init/refresh in this tab. */
function readStoredToken(key: 'token' | 'refreshToken' | 'idToken'): string | undefined {
  const value = sessionStorage.getItem(key);
  return value && value !== 'undefined' && value !== 'null' ? value : undefined;
}
